import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useHsCodeRules } from '../../../hooks/useHsCodeRules'
import { useCustomerSearch } from '../../../hooks/useCustomerSearch'
import type { HsCodeRule, HsCodeRuleInput } from '../../../hooks/useHsCodeRules'

const EMPTY_FORM: HsCodeRuleInput = { customer_id: null, from_hs_code: '', to_hs_code: '', reason: null }

export default function HSCodeRules() {
  const navigate = useNavigate()
  const { rules, loading, error, fetchRules, createRule, updateRule, deleteRule } = useHsCodeRules()

  // Filter state
  const [filterLabel, setFilterLabel] = useState('')
  const [filterCustomerId, setFilterCustomerId] = useState<string | undefined>(undefined)
  const [showFilterDrop, setShowFilterDrop] = useState(false)
  const { results: filterResults, setQuery: setFilterQuery } = useCustomerSearch()
  const filterRef = useRef<HTMLDivElement>(null)

  // Modal state
  const [showModal, setShowModal] = useState(false)
  const [editingRule, setEditingRule] = useState<HsCodeRule | null>(null)
  const [form, setForm] = useState<HsCodeRuleInput>(EMPTY_FORM)
  const [formCustomerLabel, setFormCustomerLabel] = useState('')
  const [showFormDrop, setShowFormDrop] = useState(false)
  const { results: formCustomerResults, setQuery: setFormCustomerQuery } = useCustomerSearch()
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  useEffect(() => { fetchRules(filterCustomerId) }, [fetchRules, filterCustomerId])

  // Close filter dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) setShowFilterDrop(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  function openAdd() {
    setEditingRule(null)
    setForm(EMPTY_FORM)
    setFormCustomerLabel('')
    setSaveError('')
    setShowModal(true)
  }

  function openEdit(rule: HsCodeRule) {
    setEditingRule(rule)
    setForm({ customer_id: rule.customer_id, from_hs_code: rule.from_hs_code, to_hs_code: rule.to_hs_code, reason: rule.reason })
    setFormCustomerLabel(rule.customer?.name ?? '')
    setSaveError('')
    setShowModal(true)
  }

  async function handleSave() {
    if (!form.from_hs_code.trim() || !form.to_hs_code.trim()) {
      setSaveError('From and To HS codes are required')
      return
    }
    setSaving(true)
    setSaveError('')
    try {
      if (editingRule) {
        await updateRule(editingRule.id, form)
      } else {
        await createRule(form)
      }
      setShowModal(false)
      fetchRules(filterCustomerId)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleToggleActive(rule: HsCodeRule) {
    try { await updateRule(rule.id, { is_active: !rule.is_active }); fetchRules(filterCustomerId) } catch { /* ignore */ }
  }

  async function handleDelete(rule: HsCodeRule) {
    if (!confirm(`Delete rule ${rule.from_hs_code} → ${rule.to_hs_code}?`)) return
    try { await deleteRule(rule.id); fetchRules(filterCustomerId) } catch { /* ignore */ }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <button onClick={() => navigate('/customs')}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">HS Code Replacement Rules</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Customer-instructed HS code corrections — applied as suggestions during invoice review</p>
          </div>
          <button onClick={openAdd}
            className="ml-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors">
            + Add Rule
          </button>
        </div>

        {/* Filter bar */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6 flex items-end gap-4">
          <div className="relative" ref={filterRef}>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Filter by Customer</label>
            <input
              type="text"
              placeholder="Type to search..."
              value={filterLabel}
              onChange={e => { setFilterLabel(e.target.value); setFilterQuery(e.target.value); setShowFilterDrop(true) }}
              onFocus={() => filterLabel && setShowFilterDrop(true)}
              className="w-64 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            />
            {showFilterDrop && filterResults.length > 0 && (
              <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filterResults.map(c => (
                  <button key={c.id}
                    onMouseDown={() => { setFilterCustomerId(c.id); setFilterLabel(c.name); setShowFilterDrop(false) }}
                    className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm text-gray-900 dark:text-white">
                    {c.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {filterCustomerId && (
            <button onClick={() => { setFilterCustomerId(undefined); setFilterLabel('') }}
              className="px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
              Clear
            </button>
          )}
          <div className="ml-auto text-sm text-gray-500 dark:text-gray-400">
            {rules.length} rule{rules.length !== 1 ? 's' : ''}{filterCustomerId ? ' for this customer' : ''}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">{error}</div>
        )}

        {/* Rules table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Loading...</p>
            </div>
          ) : rules.length === 0 ? (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400">
              <p className="text-lg font-medium mb-1">No rules yet</p>
              <p className="text-sm">Add a rule to auto-suggest HS code corrections during invoice review</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  {['Customer', 'From HS Code', '', 'To HS Code', 'Reason', 'Active', 'Actions'].map((h, i) => (
                    <th key={i} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rules.map(rule => (
                  <tr key={rule.id} className={`${!rule.is_active ? 'opacity-50' : ''} hover:bg-gray-50 dark:hover:bg-gray-700/50`}>
                    <td className="px-4 py-3">
                      {rule.customer ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                          {rule.customer.name}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                          Global
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono font-semibold text-red-700 dark:text-red-400">{rule.from_hs_code}</td>
                    <td className="px-4 py-3 text-gray-400">→</td>
                    <td className="px-4 py-3 font-mono font-semibold text-green-700 dark:text-green-400">{rule.to_hs_code}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-xs truncate" title={rule.reason ?? undefined}>{rule.reason || '—'}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleToggleActive(rule)}
                        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full transition-colors focus:outline-none ${rule.is_active ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}>
                        <span className={`inline-block h-4 w-4 mt-0.5 transform rounded-full bg-white shadow transition-transform ${rule.is_active ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(rule)} className="px-2 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded">Edit</button>
                        <button onClick={() => handleDelete(rule)} className="px-2 py-1 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={e => { if (e.target === e.currentTarget) setShowModal(false) }}>
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full p-6 shadow-xl">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{editingRule ? 'Edit Rule' : 'Add HS Code Rule'}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">When this HS code appears in invoice review, the replacement is suggested automatically.</p>

            {saveError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">{saveError}</div>
            )}

            <div className="space-y-4">
              {/* Customer */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Customer <span className="text-gray-400 font-normal text-xs">(blank = global)</span>
                </label>
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={formCustomerLabel}
                  onChange={e => {
                    setFormCustomerLabel(e.target.value)
                    setFormCustomerQuery(e.target.value)
                    setShowFormDrop(true)
                    if (!e.target.value) setForm(f => ({ ...f, customer_id: null }))
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
                {showFormDrop && formCustomerResults.length > 0 && (
                  <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                    {formCustomerResults.map(c => (
                      <button key={c.id}
                        onMouseDown={() => { setForm(f => ({ ...f, customer_id: c.id })); setFormCustomerLabel(c.name); setShowFormDrop(false) }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm text-gray-900 dark:text-white">
                        {c.name}
                      </button>
                    ))}
                  </div>
                )}
                {form.customer_id && <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">Customer-specific rule</p>}
              </div>

              {/* From / To */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From HS Code <span className="text-red-500">*</span></label>
                  <input type="text" value={form.from_hs_code} onChange={e => setForm(f => ({ ...f, from_hs_code: e.target.value }))}
                    placeholder="e.g. 6117900000"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">To HS Code <span className="text-red-500">*</span></label>
                  <input type="text" value={form.to_hs_code} onChange={e => setForm(f => ({ ...f, to_hs_code: e.target.value }))}
                    placeholder="e.g. 6117900099"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono" />
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason</label>
                <input type="text" value={form.reason || ''} onChange={e => setForm(f => ({ ...f, reason: e.target.value || null }))}
                  placeholder="e.g. Customer ABC instructed replacement on 2026-05-01"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors">
                {saving ? 'Saving...' : editingRule ? 'Save Changes' : 'Add Rule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
